<template>
  <div class="opc-cart" :class="{
    'opc-cart--no-title': !showTitle,
    'opc-modal': $context.is.modal
  }">
    <singleton>
      <prorated-main />
    </singleton>
    <div class="opc-cart__upper">
      <support-coupon-banner />
      <div v-if="showTitle" class="opc-cart__title text-capitalize">
        <h2>{{ title }}</h2>
        <template v-if="!onlyShowTitle">
          <ShareLink v-if="showShareLinkAfterTitle" />
          <currency v-if="$context.is.switchCurrency || !$config('hideCurrency', false)" />
        </template>
      </div>
      <div class="opc-cart__main">
        <plan-count v-if="$context.is.desktop" />
        <threshold-coupon-process-bar />
        <zcm-domain-warning />
        <product-details class='opc-cart__main__cartinfo __PLACEHOLDER_cart__' render-price :filter-keys="filterKeys" @edit="onEdit" @remove="onRemove" />
        <downsell-tip />
        <partner-plan-cart />
        <open-invoice-cart
          :open-invoice-list="openInvoiceList"
          :open-invoice-total-amount="openInvoiceTotalAmount"
          :open-invoice-original-total-amount="openInvoiceOriginalTotalAmount"
          :current-credit-balance="getterCurrentCreditBalance"
        />
        <div v-if="isCurrentMode">
          <alt-prorated-current-summary>
            <div class="opc-cart__main__divider"></div>
            <current-charge />
          </alt-prorated-current-summary>
        </div>
        <div class='__PLACEHOLDER_total__' v-else>
          <product-details render-agreement :filter-keys="filterKeys" />
          <product-details render-tip :filter-keys="filterKeys" />
          <prorated-charge v-if="showProratedCharge" />
          <div class="opc-cart__main__divider" v-if="getSelectedPlans.length"></div>
          <refer-a-friend-discount-alert />
          <coupon />
          <alt-prorated-total>
            <total />
          </alt-prorated-total>
          <div v-if="getSelectedPlans.length">
          <div class="opc-cart__main__divider" v-if="!showProratedCharge"></div>
          <next-auto-renewal v-if="shouldShowNextAutoRenewal" />
          <next-charge v-else-if="shouldShowNextCharge" />
          <!-- placeholder for new confirmation page -->
          <div v-show='false' class='__PLACEHOLDER_0__'></div>
          <alt-prorated-description>
            <description />
          </alt-prorated-description>
          <alt-prorated-updated-summary />
          </div>
        </div>
      </div>
    </div>
    <div class="opc-cart__footer" v-if="$context.is.desktop">
      <div class="opc-cart__submit __PLACEHOLDER_submit__">
        <alt-todays-total />
        <slot name="action" />
      </div>
    </div>
  </div>
</template>

<script>
import ProductDetails from './details.vue';
import ProratedCharge from './prorated-charge.vue';
import Total from './total.vue';
import Coupon from '@/views/config/coupon/index.vue';
import Description from './description.vue';
import { mapGetters, mapMutations } from 'vuex';
import Currency from './currency.vue';
import NextCharge from './next.vue';
import nextMixin from './nextMixin';
import PlanCount from '@/views/config/shopping-cart/plan-count';
import CurrentCharge from './current.vue';
import ShareLink from './share-link.vue';
import ProratedMain from '@/views/config/shopping-cart/prorated/index.vue';
import AltProratedDescription from '@/views/config/shopping-cart/prorated/description.vue';
import AltProratedCurrentSummary from '@/views/config/shopping-cart/prorated/summary-current.vue';
import AltProratedUpdatedSummary from '@/views/config/shopping-cart/prorated/summary-updated.vue';
import AltProratedTotal from '@/views/config/shopping-cart/prorated/total.vue';
import PartnerPlanCart from '@/views/config/partner-refer/plans/cart.vue';
import Singleton from '@/components/singleton.vue';
import OpenInvoiceCart from '@/views/config/shopping-cart/open-invoice/open-invoice-cart.vue';
import SupportCouponBanner from '@/views/config/coupon/component/support-coupon/redeem-baner.vue';
import AltTodaysTotal from './alt-todays-total.vue';
import { shoppingCartPlanInitReady } from '@/utils/ready';
import NextAutoRenewal from './next-auto-renewal.vue';
import { BASE_PLAN_SUB_TYPES, BILLING_CYCLE, enableProAnnualMonthlyPaymentPlanP2 } from '@/common/constants';
import DownsellTip from './downsell-tip.vue';
import ThresholdCouponProcessBar from '@/views/config/coupon/component/threshold-coupon/process-bar.vue';
import ZcmDomainWarning from './zcm-domain-warning.vue';
import ReferAFriendDiscountAlert from '@/views/config/shopping-cart/refer-a-friend-discount/refer-a-friend-discount-alert.vue';

export default {
  data() {
    return {
      enableProAnnualMonthlyPaymentPlanP2,
    }
  },
  mixins: [nextMixin],
  components: {
    SupportCouponBanner,
    OpenInvoiceCart,
    PartnerPlanCart,
    PlanCount,
    Currency,
    ProductDetails,
    ProratedCharge,
    Total,
    CurrentCharge,
    Description,
    Coupon,
    NextCharge,
    ShareLink,
    ProratedMain,
    AltProratedDescription,
    AltProratedUpdatedSummary,
    AltProratedCurrentSummary,
    AltProratedTotal,
    Singleton,
    AltTodaysTotal,
    NextAutoRenewal,
    DownsellTip,
    ZcmDomainWarning,
    ThresholdCouponProcessBar,
    ReferAFriendDiscountAlert,
  },
  props: {
    filterKeys: {
      type: Array,
      default: () => [],
    },
    showTitle: {
      type: Boolean,
      default: true,
    },
    onlyShowTitle: {
      type: Boolean,
      default: false,
    },
    showShareLinkAfterTitle: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters([
      'getSelectedPlans',
      'isLoadingTax',
      'isSelectedSameAsEditablePlansInBought',
      'fromCampaignLink',
      'mixBuyFlowContainsEditCase',
      'migratePlanState',
      'getProratedChargeTotalAmount',
      'isLoadingPreview',
      'isInrCitEligible',
      'isProratedChargeAmountCalculated',
      'openInvoiceList',
      'openInvoiceTotalAmount',
      'openInvoiceOriginalTotalAmount',
      'getterCurrentCreditBalance',
      'includeTaxTotalAmount',
      'haveVerifiedSupportCoupon',
      'accountWithCouponDiscount',
      'couponState'
    ]),
    isSelectedPlansOnlyContainAnnualTermMonthlyProPlan() {
      return this.getSelectedPlans.length === 1 && !!this.getSelectedPlans.find(plan => plan.category === BASE_PLAN_SUB_TYPES.PRO && plan.billing_cycle === BILLING_CYCLE.ANNUAL_TERM_MONTHLY);
    },
    isCurrentMode() {
      if(this.$context.is.align) return false;
      // display as current plans mode or updated mode
      return this.$context.is.edit && this.isSelectedSameAsEditablePlansInBought && !this.fromCampaignLink && !this.haveVerifiedSupportCoupon;
    },
    isShowNextCharge() {
      // videosdk ft plan no need to show next charge
      return !this.getSelectedPlans.every(plan => plan?.trialDays >= 360);
    },
    plansCount() {
      return this.getSelectedPlans.length;
    },
    title() {
      if (this.$context.is.delta) return this.$t("billing.order_summary");
      if (this.migratePlanState.is.upgrade) {
        return this.$t('billing.order_summary_updated');
      }
      if (this.$context.is.edit) {
        if (this.isCurrentMode) {
          return this.$t('billing.order_summary_current');
        } else {
          return this.$t('billing.order_summary_updated');
        }
      }
      if(this.$context.is.downgrade || this.$context.is.upgrade) {
        return this.$t('billing.order_summary_updated');
      }

      if (this.$context.is.mixBuy && this.mixBuyFlowContainsEditCase) {
        return this.$t('billing.order_summary_updated');
      }

      return this.$t('billing.order_summary');
    },
    showProratedCharge() {
      if (this.$context.is.delta) return true;
      return this.isInrCitEligible && this.isProratedChargeAmountCalculated;
    },
    // Common context conditions for next charge components
    isNextChargeContext() {
      return this.$context.is.buyNow || this.$context.is.basic2pro || this.$context.is.add2trial || this.$context.is.buyTrial;
    },
    isBuyNowOrBasic2ProContext() {
      return this.$context.is.buyNow || this.$context.is.basic2pro;
    },
    shouldShowNextAutoRenewal() {
      return this.enableProAnnualMonthlyPaymentPlanP2 &&
        this.isBuyNowOrBasic2ProContext &&
        this.isSelectedPlansOnlyContainAnnualTermMonthlyProPlan;
    },
    shouldShowNextCharge() {
      return this.isNextChargeContext && this.isShowNextCharge;
    }
  },
  methods: {
    ...mapMutations(['updateSelectedPlansMap0']),
    onEdit(...args) {
      this.$emit('edit', ...args);
    },
    onRemove(...args) {
      this.$emit('remove', ...args);
    },
  },
  created(){
    shoppingCartPlanInitReady.ready(()=>{
      this.$nextTick(()=>{
        this.updateSelectedPlansMap0();
      })
    })
  }
};
</script>

<style lang="scss">
@import "./index.scss";
</style>
